import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiBaseUrl } from "../../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../../utils/profilePicUrl";
import { isGroupAdmin } from "../../utils/groupRole";
import Loading from "../icon/loading";
import "./groupdesign/GroupMembers.css";

const InviteUser = ({ user, onRefresh, groupId: groupIdProp, groupLocked: groupLockedProp }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [message, setMessage] = useState("");
  const [members, setMembers] = useState([]);
  const { groupId: groupIdParam } = useParams();
  const navigate = useNavigate();
  const groupId = groupIdProp || groupIdParam;
  const [requestSent, setRequestSent] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [groupLocked, setGroupLocked] = useState(Boolean(groupLockedProp));
  const API_URL = getApiBaseUrl();

  const togglePopup = () => {
    setShowPopup(!showPopup);
    setMessage("");
    setQuery("");
    setSuggestions([]);
    setSelectedUserId(null);
  };

  const searchUsers = async (q) => {
    setQuery(q);
    setSelectedUserId(null);
    setMessage("");
    if (q.length < 2) return setSuggestions([]);
    try {
      const groupParam = groupId ? `&group_id=${groupId}` : "";
      const viewerParam = user?.id ? `&viewer_id=${user.id}` : "";
      const res = await fetch(
        `${API_URL}/search_users.php?q=${encodeURIComponent(q)}${groupParam}${viewerParam}`
      );
      const data = await res.json();
      if (data.success) setSuggestions(data.users);
    } catch (err) {
      console.error(err);
    }
  };

  const getDisplayName = (u) => {
    const full = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
    return full || u.username || u.email || "Member";
  };

  const getRoleLabel = (member) => {
    const raw = String(member.role || member.typeofuser || "member")
      .trim()
      .toLowerCase();
    if (raw === "admin" || raw === "owner") return "Admin";
    return "Member";
  };

  const selectUser = (u) => {
    setSelectedUserId(u.id);
    setQuery(u.username);
    setMessage("");
  };

  const handleInvite = async () => {
    if (groupLocked) {
      return setMessage("This group is deactivated.");
    }
    if (!groupId || !selectedUserId || !user?.id) {
      return setMessage("Missing group, invitee, or your account.");
    }
    try {
      const res = await fetch(`${API_URL}/invite_user_to_group.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_id: groupId,
          user_id: selectedUserId,
          invited_by: user.id,
        }),
      });
      const data = await res.json();
      setMessage(data.message || (data.success ? "Invite sent." : "Invite failed."));
      if (data.success) {
        setSuggestions((prev) => prev.filter((u) => u.id !== selectedUserId));
        setSelectedUserId(null);
        fetchGroupMembers();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      setMessage("Error inviting user.");
    }
  };

  const handleJoinGroup = async () => {
    if (!groupId || !user?.id) return;
    if (groupLocked) {
      alert("This group is deactivated.");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/join_group.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: groupId, user_id: user.id }),
      });
      const data = await res.json();
      alert(data.message);
      if (data.success) {
        setRequestSent(true);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      alert("Error joining the group.");
    }
  };

  const handleCancelRequest = async () => {
    if (!groupId || !user?.id) return;
    try {
      const res = await fetch(`${API_URL}/cancel_join_request.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_id: groupId,
          user_id: user.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setRequestSent(false);
        alert(data.message);
        if (onRefresh) onRefresh();
      } else {
        alert(data.message || "Failed to cancel request");
      }
    } catch (err) {
      console.error("Cancel request error:", err);
      alert("Network or server error cancelling the request.");
    }
  };

  const handleLeaveGroup = async () => {
    if (!groupId || !user?.id || leaving) return;

    const userIsGroupAdmin = isGroupAdmin(user, members);
    const confirmMessage = userIsGroupAdmin
      ? "Are you sure you want to leave this group? The first member you added will become the new admin."
      : "Are you sure you want to leave this group?";
    if (!window.confirm(confirmMessage)) return;

    setLeaving(true);
    try {
      const res = await fetch(`${API_URL}/leave_group.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: groupId, user_id: user.id }),
      });
      const data = await res.json();
      if (!data.success) {
        setLeaving(false);
        alert(data.message || "Failed to leave the group.");
        return;
      }

      navigate("/home", { replace: true });
    } catch (err) {
      console.error(err);
      setLeaving(false);
      alert("Error leaving the group.");
    }
  };

  const fetchGroupMembers = async () => {
    try {
      const res = await fetch(
        `${API_URL}/get_group_members.php?group_id=${groupId}`
      );
      const data = await res.json();
      if (data.success) setMembers(data.members);
    } catch (err) {
      console.error("Error fetching group members:", err);
    }
  };

  const fetchGroupStatus = async () => {
    if (!groupId || groupLockedProp) return;
    try {
      const res = await fetch(`${API_URL}/get_group_info.php?group_id=${groupId}`);
      const data = await res.json();
      if (data.success) {
        setGroupLocked(Boolean(data.group?.is_deactivated || data.group?.pending_delete));
      }
    } catch (err) {
      console.error("Error fetching group status:", err);
    }
  };

  useEffect(() => {
    setGroupLocked(Boolean(groupLockedProp));
  }, [groupLockedProp]);

  useEffect(() => {
    if (groupId) {
      fetchGroupMembers();
      fetchGroupStatus();
    }
  }, [groupId]);

  const isMember =
    user &&
    Array.isArray(members) &&
    members.some((m) => Number(m.id) === Number(user.id));

  const messageIsError =
    /fail|error|missing|⚠️|❌/i.test(String(message || "")) ||
    message.includes("⚠️") ||
    message.includes("❌");

  return (
    <div className="gm-panel">
      {leaving ? (
        <div className="gm-leaving-overlay" aria-live="polite" aria-busy="true">
          <Loading />
          <p>Leaving group...</p>
        </div>
      ) : null}
      {user && (
        <div className="gm-actions">
          {isMember ? (
            <>
              <button
                type="button"
                className="gm-btn gm-btn--invite"
                onClick={togglePopup}
                disabled={leaving || groupLocked}
                title={groupLocked ? "This group is deactivated" : undefined}
              >
                Invite user
              </button>
              <button
                type="button"
                className="gm-btn gm-btn--leave"
                onClick={handleLeaveGroup}
                disabled={leaving}
              >
                {leaving ? "Leaving..." : "Leave group"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={`gm-btn ${requestSent ? "gm-btn--leave" : "gm-btn--join"}`}
              onClick={requestSent ? handleCancelRequest : handleJoinGroup}
              disabled={groupLocked && !requestSent}
              title={groupLocked ? "This group is deactivated" : undefined}
            >
              {requestSent ? "Cancel request" : groupLocked ? "Group deactivated" : "Join group"}
            </button>
          )}
        </div>
      )}

      <section className="gm-card" aria-label="Group members">
        <div className="gm-card__header">
          <h3>Group Members</h3>
          <span className="gm-card__count">
            {members.length} {members.length === 1 ? "member" : "members"}
          </span>
        </div>

        {members.length === 0 ? (
          <div className="gm-empty">
            <strong>No members yet</strong>
            Invite people to grow this group.
          </div>
        ) : (
          <ul className="gm-list">
            {members.map((member) => {
              const role = getRoleLabel(member);
              const isAdminRole = role === "Admin";
              return (
                <li key={member.id} className="gm-member">
                  <img
                    className="gm-avatar"
                    src={resolveAvatarUrl(member, API_URL)}
                    alt=""
                    onError={handleAvatarError}
                  />
                  <div className="gm-member__info">
                    <div className="gm-member__name">{getDisplayName(member)}</div>
                    {(member.username || member.email) && (
                      <div className="gm-member__meta">
                        {member.username ? `@${member.username}` : member.email}
                      </div>
                    )}
                  </div>
                  <span
                    className={`gm-role ${isAdminRole ? "gm-role--admin" : "gm-role--member"}`}
                  >
                    {role}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {showPopup && (
        <>
          <div className="gm-overlay" onClick={togglePopup} role="presentation" />
          <div
            className="gm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gm-invite-title"
          >
            <h3 id="gm-invite-title">Invite user to group</h3>
            <p className="gm-modal__sub">Search and select a person to invite</p>

            <input
              type="search"
              className="gm-search"
              value={query}
              onChange={(e) => searchUsers(e.target.value)}
              placeholder="Search by name, username, or email"
              autoFocus
              aria-label="Search users to invite"
            />

            <div className="gm-suggest">
              {query.length < 2 ? (
                <p className="gm-suggest__hint">Type at least 2 characters to find people</p>
              ) : suggestions.length === 0 ? (
                <p className="gm-suggest__hint">No matching users found</p>
              ) : (
                <ul className="gm-suggest-list">
                  {suggestions.map((u) => {
                    const selected = selectedUserId === u.id;
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          className={`gm-suggest-item${selected ? " is-selected" : ""}`}
                          onClick={() => selectUser(u)}
                        >
                          <img
                            className="gm-avatar"
                            src={resolveAvatarUrl(u, API_URL)}
                            alt=""
                            onError={handleAvatarError}
                          />
                          <div className="gm-member__info">
                            <div className="gm-member__name">{getDisplayName(u)}</div>
                            <div className="gm-member__meta">
                              @{u.username}
                              {u.email ? ` · ${u.email}` : ""}
                            </div>
                          </div>
                          {selected ? <span className="gm-suggest-check">✓</span> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {message ? (
              <p
                className={`gm-message ${messageIsError ? "gm-message--err" : "gm-message--ok"}`}
              >
                {message}
              </p>
            ) : null}

            <div className="gm-modal__actions">
              <button type="button" className="gm-btn gm-btn--ghost" onClick={togglePopup}>
                Close
              </button>
              <button
                type="button"
                className="gm-btn gm-btn--primary"
                onClick={handleInvite}
                disabled={!selectedUserId}
              >
                Invite
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InviteUser;
